import {StageLayer} from "@nolimitcity/slot-launcher/bin/StageLayer";
import {ImgLoader} from "@nolimitcity/slot-launcher/bin/loader/ImgLoader";
import {ContinueButton} from "@nolimitcity/slot-launcher/bin/gui/buttons/concretebuttons/ContinueButton";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {gsap} from "gsap";
import {KennethMustDie} from "../KennethMustDie";
import {KMDSound} from "../sound/KMDSound";

export class KMDPreIntro extends PIXI.Container implements StageLayer {
    private readonly _button: ContinueButton;
    private _completed: boolean;
    private _startIntroCallback: (value: any) => void;
    private _tl: gsap.core.Timeline;

    constructor() {
        super();

        this._completed = false;

        const bg: PIXI.Graphics = new PIXI.Graphics().beginFill(0x000000, 1).drawRect(-480, -480, 1680, 1680).endFill(),
            preIntro1: PIXI.Sprite = new PIXI.Sprite(ImgLoader.getImgTexture("preIntro1")),
            preIntro2: PIXI.Sprite = new PIXI.Sprite(ImgLoader.getImgTexture("preIntro2"));

        const glitch: PIXI.Sprite = new PIXI.Sprite(ImgLoader.getImgTexture("preIntroGlitch3"));

        preIntro1.visible = false;
        preIntro1.anchor.set(0.5);
        preIntro1.position.set(360, 300);

        preIntro2.anchor.set(0.5);

        glitch.alpha = 0;
        glitch.anchor.set(0.5);
        glitch.scale.set(3.5);
        glitch.blendMode = PIXI.BLEND_MODES.MULTIPLY;

        preIntro2.position = glitch.position = preIntro1.position;

        this._button = new ContinueButton();
        this._button.enable(true);
        this._button.pivot.set(this._button.width * 0.5, this._button.height * 0.5);
        this._button.addClickCallback(() => {
            this.startIntro();

            // #247
            KennethMustDie.soundPlayer.playSound(KMDSound.LOADER_CONTINUE_BUTTON);
        });

        this.addChild(bg, preIntro2, preIntro1, glitch, this._button);

        this.onResize();

        this._tl = gsap.timeline({
            paused: true,
            delay: 1,
            repeat: -1
        });
        this.preIntro1Visibility(preIntro1);
        this.glitchVisibility(glitch);
        this.glitchImageChangeGroup1(glitch);
        this.glitchImageChangeGroup2(glitch);
        this.glitchImageChangeGroup3(glitch);
        this.glitchImageChangeGroup4(glitch);

        // 2 second hold before it starts looping itself
        this._tl.to(this, {
            duration: 2
        });
    }

    public onResize(): void {
        if (NolimitApplication.isLandscape) {
            this._button.position.set(360, 615);
        } else {
            const centerBottom: number = (NolimitApplication.screenBounds.bottom - 640) * 0.5 + 640;
            this._button.position.set(360, centerBottom);
        }
    }

    public show(): void {
        NolimitApplication.addLayer("PRE_INTRO", this);

        // #247
        KennethMustDie.soundPlayer.switchMusic("loaderLoop");
    }

    public complete(): Promise<any> {
        if (this._completed) {
            return Promise.resolve();
        }

        this._tl.play();

        return new Promise((resolve) => {
            this._startIntroCallback = resolve;
        });
    }

    private startIntro(): void {
        this._button.enable(false);
        this._completed = true;
        if (this._startIntroCallback) {
            this._startIntroCallback(undefined);
        }

        const tl: gsap.core.Timeline = gsap.timeline({
            delay: 0.1
        });
        tl.to(this, {duration: 0.2, alpha: 0});
        tl.add(() => {
            NolimitApplication.removeLayer("PRE_INTRO");

            this._tl.pause();
            this._tl.kill();
        });
    }

    private preIntro1Visibility(preIntro1: PIXI.Sprite): void {
        this._tl.add(() => {
            preIntro1.visible = true;
        }, 0.5);
        this._tl.add(() => {
            preIntro1.visible = false;
        }, 1.5667);
        this._tl.add(() => {
            preIntro1.visible = true;
        }, 1.7667);
        this._tl.add(() => {
            preIntro1.visible = false;
        }, 2.7333);
        this._tl.add(() => {
            preIntro1.visible = true;
        }, 3.0667);
    }

    private glitchVisibility(glitch: PIXI.Sprite): void {
        this._tl.to(glitch, {
            duration: 0.2 - 0.1667,
            alpha: 1
        }, 0.1667);
        this._tl.to(glitch, {
            duration: 0.3333 - 0.3,
            alpha: 0
        }, 0.3);
        this._tl.to(glitch, {
            duration: 0.5 - 0.4667,
            alpha: 1
        }, 0.4667);
        this._tl.to(glitch, {
            duration: 0.6667 - 0.6333,
            alpha: 0
        }, 0.6333);
        this._tl.to(glitch, {
            duration: 1.3 - 1.2667,
            alpha: 1
        }, 1.2667);
        this._tl.to(glitch, {
            duration: 1.6 - 1.5667,
            alpha: 0
        }, 1.5667);
        this._tl.to(glitch, {
            duration: 1.7667 - 1.7333,
            alpha: 1
        }, 1.7333);
        this._tl.to(glitch, {
            duration: 1.8333 - 1.7667,
            alpha: 0
        }, 1.7667);
        this._tl.to(glitch, {
            duration: 2.7 - 2.6667,
            alpha: 1
        }, 2.6667);
        this._tl.to(glitch, {
            duration: 2.8 - 2.7,
            alpha: 0
        }, 2.7);
        this._tl.to(glitch, {
            duration: 3.0667 - 3.0333,
            alpha: 1
        }, 3.0333);
        this._tl.to(glitch, {
            duration: 3.2333 - 3.2,
            alpha: 0
        }, 3.2);
    }

    private glitchImageChangeGroup1(glitch: PIXI.Sprite): void {
        this._tl.add(() => {
            glitch.texture = ImgLoader.getImgTexture("preIntroGlitch8");
        }, 0.1667);
        this._tl.add(() => {
            glitch.texture = ImgLoader.getImgTexture("preIntroGlitch9");
        }, 0.2);
        this._tl.add(() => {
            glitch.texture = ImgLoader.getImgTexture("preIntroGlitch10");
        }, 0.2333);
        this._tl.add(() => {
            glitch.texture = ImgLoader.getImgTexture("preIntroGlitch11");
        }, 0.2667);
        this._tl.add(() => {
            glitch.texture = ImgLoader.getImgTexture("preIntroGlitch12");
        }, 0.3);
        this._tl.add(() => {
            glitch.texture = ImgLoader.getImgTexture("preIntroGlitch13");
        }, 0.3333);
        this._tl.add(() => {
            glitch.texture = ImgLoader.getImgTexture("preIntroGlitch14");
        }, 0.3667);
        this._tl.add(() => {
            glitch.texture = ImgLoader.getImgTexture("preIntroGlitch15");
        }, 0.4);
        this._tl.add(() => {
            glitch.texture = ImgLoader.getImgTexture("preIntroGlitch16");
        }, 0.4333);
        this._tl.add(() => {
            glitch.texture = ImgLoader.getImgTexture("preIntroGlitch17");
        }, 0.4667);
    }

    private glitchImageChangeGroup2(glitch: PIXI.Sprite): void {
        this._tl.add(() => {
            glitch.texture = ImgLoader.getImgTexture("preIntroGlitch3");
        }, 0.5);
        this._tl.add(() => {
            glitch.texture = ImgLoader.getImgTexture("preIntroGlitch4");
        }, 0.5333);
        this._tl.add(() => {
            glitch.texture = ImgLoader.getImgTexture("preIntroGlitch5");
        }, 0.5667);
        this._tl.add(() => {
            glitch.texture = ImgLoader.getImgTexture("preIntroGlitch6");
        }, 0.6);
        this._tl.add(() => {
            glitch.texture = ImgLoader.getImgTexture("preIntroGlitch7");
        }, 0.6333);
        this._tl.add(() => {
            glitch.texture = ImgLoader.getImgTexture("preIntroGlitch8");
        }, 0.6667);
        this._tl.add(() => {
            glitch.texture = ImgLoader.getImgTexture("preIntroGlitch9");
        }, 1.2667);
        this._tl.add(() => {
            glitch.texture = ImgLoader.getImgTexture("preIntroGlitch10");
        }, 1.3);
        this._tl.add(() => {
            glitch.texture = ImgLoader.getImgTexture("preIntroGlitch11");
        }, 1.3333);
        this._tl.add(() => {
            glitch.texture = ImgLoader.getImgTexture("preIntroGlitch12");
        }, 1.3667);
        this._tl.add(() => {
            glitch.texture = ImgLoader.getImgTexture("preIntroGlitch13");
        }, 1.4);
        this._tl.add(() => {
            glitch.texture = ImgLoader.getImgTexture("preIntroGlitch14");
        }, 1.4333);
        this._tl.add(() => {
            glitch.texture = ImgLoader.getImgTexture("preIntroGlitch15");
        }, 1.4667);
        this._tl.add(() => {
            glitch.texture = ImgLoader.getImgTexture("preIntroGlitch16");
        }, 1.5);
        this._tl.add(() => {
            glitch.texture = ImgLoader.getImgTexture("preIntroGlitch17");
        }, 1.5333);
    }

    private glitchImageChangeGroup3(glitch: PIXI.Sprite): void {
        this._tl.add(() => {
            glitch.texture = ImgLoader.getImgTexture("preIntroGlitch3");
        }, 1.5667);
        this._tl.add(() => {
            glitch.texture = ImgLoader.getImgTexture("preIntroGlitch4");
        }, 1.6);
        this._tl.add(() => {
            glitch.texture = ImgLoader.getImgTexture("preIntroGlitch5");
        }, 1.6333);
        this._tl.add(() => {
            glitch.texture = ImgLoader.getImgTexture("preIntroGlitch6");
        }, 1.6667);
        this._tl.add(() => {
            glitch.texture = ImgLoader.getImgTexture("preIntroGlitch7");
        }, 1.7);
        this._tl.add(() => {
            glitch.texture = ImgLoader.getImgTexture("preIntroGlitch8");
        }, 1.7333);
        this._tl.add(() => {
            glitch.texture = ImgLoader.getImgTexture("preIntroGlitch9");
        }, 1.7667);
        this._tl.add(() => {
            glitch.texture = ImgLoader.getImgTexture("preIntroGlitch10");
        }, 1.8);
        this._tl.add(() => {
            glitch.texture = ImgLoader.getImgTexture("preIntroGlitch11");
        }, 1.8333);
        this._tl.add(() => {
            glitch.texture = ImgLoader.getImgTexture("preIntroGlitch16");
        }, 2.6667);
        this._tl.add(() => {
            glitch.texture = ImgLoader.getImgTexture("preIntroGlitch17");
        }, 2.7);
    }

    private glitchImageChangeGroup4(glitch: PIXI.Sprite): void {
        this._tl.add(() => {
            glitch.texture = ImgLoader.getImgTexture("preIntroGlitch3");
        }, 2.7333);
        this._tl.add(() => {
            glitch.texture = ImgLoader.getImgTexture("preIntroGlitch4");
        }, 2.7667);
        this._tl.add(() => {
            glitch.texture = ImgLoader.getImgTexture("preIntroGlitch5");
        }, 2.8);
        this._tl.add(() => {
            glitch.texture = ImgLoader.getImgTexture("preIntroGlitch6");
        }, 2.8333);
        this._tl.add(() => {
            glitch.texture = ImgLoader.getImgTexture("preIntroGlitch7");
        }, 2.8667);
        this._tl.add(() => {
            glitch.texture = ImgLoader.getImgTexture("preIntroGlitch8");
        }, 2.9);
        this._tl.add(() => {
            glitch.texture = ImgLoader.getImgTexture("preIntroGlitch9");
        }, 2.9333);
        this._tl.add(() => {
            glitch.texture = ImgLoader.getImgTexture("preIntroGlitch10");
        }, 2.9667);
        this._tl.add(() => {
            glitch.texture = ImgLoader.getImgTexture("preIntroGlitch11");
        }, 3);
        this._tl.add(() => {
            glitch.texture = ImgLoader.getImgTexture("preIntroGlitch12");
        }, 3.0333);
        this._tl.add(() => {
            glitch.texture = ImgLoader.getImgTexture("preIntroGlitch13");
        }, 3.0667);
        this._tl.add(() => {
            glitch.texture = ImgLoader.getImgTexture("preIntroGlitch14");
        }, 3.1);
        this._tl.add(() => {
            glitch.texture = ImgLoader.getImgTexture("preIntroGlitch15");
        }, 3.1333);
        this._tl.add(() => {
            glitch.texture = ImgLoader.getImgTexture("preIntroGlitch16");
        }, 3.1667);
        this._tl.add(() => {
            glitch.texture = ImgLoader.getImgTexture("preIntroGlitch17");
        }, 3.2);
    }
}